/**
 * Live updates. The server says *that* something changed (restaurant, housekeeping or services); the screen then
 * refreshes its own data through the normal API, so it only ever learns what it is allowed to read.
 *
 * EventSource cannot send the sign-in header, so the stream is read with fetch. While it is connected the screens
 * poll only as a slow safety net; if it drops they go back to polling quickly, and reconnect on their own.
 */
import type { QueryClient } from '@tanstack/react-query';
import { currentToken, refreshSession } from '../api/client';

export type Topic = 'restaurant' | 'housekeeping' | 'services';

const KEYS: Record<Topic, string[]> = {
  restaurant: ['rs', 'menu', 'stock', 'daily'],
  housekeeping: ['hk', 'laundry'],
  services: ['pool', 'transport'],
};

/** Read by the screens' refetchInterval: true while the live channel is delivering. */
export const live = { connected: false };

/** How often a screen should poll: quickly when there is no live channel, rarely when there is. */
export const pollEvery =
  (ms: number) =>
  (): number =>
    live.connected ? 60_000 : ms;

/** Pulls whole `event: …\ndata: …` blocks out of what has arrived so far; returns the topics and the unread rest. */
export function parseEvents(buffer: string): { topics: string[]; rest: string } {
  const topics: string[] = [];
  const blocks = buffer.split('\n\n');
  const rest = blocks.pop() ?? '';
  for (const b of blocks) {
    const lines = b.split('\n');
    if (lines.includes('event: changed')) {
      const data = lines.find((l) => l.startsWith('data: '));
      if (data) topics.push(data.slice(6).trim());
    }
  }
  return { topics, rest };
}

interface Options {
  fetchImpl?: typeof fetch;
  /** wait before reconnecting; tests make this short */
  backoff?: (attempt: number) => number;
}

export function startLive(qc: QueryClient, opts: Options = {}): () => void {
  const doFetch = opts.fetchImpl ?? ((i, init) => fetch(i, init));
  const backoff = opts.backoff ?? ((n) => Math.min(15_000, 1_000 * 2 ** Math.min(n, 4)));
  const ctl = new AbortController();
  let stopped = false;
  let wake: (() => void) | null = null;
  const pending = new Set<Topic>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  // several changes in a burst refresh a screen once
  const refresh = (topic: Topic): void => {
    pending.add(topic);
    timer ??= setTimeout(() => {
      timer = null;
      for (const t of pending) for (const k of KEYS[t]) void qc.invalidateQueries({ queryKey: [k] });
      pending.clear();
    }, 120);
  };
  const refreshAll = (): void => (Object.keys(KEYS) as Topic[]).forEach(refresh);

  const onOnline = (): void => wake?.();
  window.addEventListener('online', onOnline);

  void (async () => {
    let attempt = 0;
    while (!stopped) {
      let opened = false;
      try {
        let res = await doFetch('/api/events', { headers: { Accept: 'text/event-stream', ...(currentToken() ? { Authorization: `Bearer ${currentToken()}` } : {}) }, credentials: 'same-origin', signal: ctl.signal });
        if (res.status === 401 && (await refreshSession())) {
          res = await doFetch('/api/events', { headers: { Accept: 'text/event-stream', Authorization: `Bearer ${currentToken()}` }, credentials: 'same-origin', signal: ctl.signal });
        }
        if (res.status === 404) {
          stopped = true; // the host has live updates switched off: screens poll instead, and there is no point asking again
          break;
        }
        if (!res.ok || !res.body) throw new Error(`events ${res.status}`);
        opened = true;
        live.connected = true;
        if (attempt > 0) refreshAll(); // whatever happened while we were away
        attempt = 0;
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          const parsed = parseEvents(buf + dec.decode(value, { stream: true }));
          buf = parsed.rest;
          for (const t of parsed.topics) if (t in KEYS) refresh(t as Topic);
        }
      } catch {
        /* dropped or refused: fall through to reconnect */
      }
      live.connected = false;
      if (stopped) break;
      if (!opened) attempt += 1;
      else attempt = Math.max(attempt, 1); // it worked, then ended (the server rotates streams): come straight back, then catch up
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, opened ? 300 : backoff(attempt));
        wake = () => {
          clearTimeout(t);
          resolve();
        };
      });
      wake = null;
    }
  })();

  return () => {
    stopped = true;
    live.connected = false;
    ctl.abort();
    wake?.();
    if (timer) clearTimeout(timer);
    window.removeEventListener('online', onOnline);
  };
}
